import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useOnboarding } from 'context/onboarding.context';
import { onboardingSteps, ONBOARDING_TOTAL_STEPS } from 'constants/onboarding.steps';
import useRedux from 'redux/useRedux';
import { useNavigate } from 'react-router-dom';

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8; // Padding around the highlighted element

function useTargetRect(selector: string, isActive: boolean, step: number) {
  const [rect, setRect] = useState<TargetRect | null>(null);

  useEffect(() => {
    if (!isActive) {
      setRect(null);
      return;
    }

    const measure = () => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    // First attempt immediately, then retry after submenu expand animation completes
    // SubMenu animation in react-pro-sidebar takes ~300ms; add buffer
    measure();
    const t1 = setTimeout(measure, 500);   // after first render cycle
    const t2 = setTimeout(measure, 900);   // after expand animation fully done

    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', measure);
    };
  }, [selector, isActive, step]);

  return rect;
}

export default function OnboardingTour() {
  const { isActive, currentStep, nextStep, prevStep, skipTour } = useOnboarding();
  const { storeState: { Auth } } = useRedux();
  const navigate = useNavigate();

  const step = onboardingSteps[currentStep];
  const rect = useTargetRect(step?.targetSelector ?? '', isActive, currentStep);

  if (!isActive || !step) return null;

  const highlightTop = rect ? rect.top - PADDING : 0;
  const highlightLeft = rect ? rect.left - PADDING : 0;
  const highlightWidth = rect ? rect.width + PADDING * 2 : 0;
  const highlightHeight = rect ? rect.height + PADDING * 2 : 0;

  // Tooltip placement (always to the right of the sidebar)
  const tooltipLeft = rect ? rect.left + rect.width + PADDING + 16 : 260;
  const tooltipTop = rect ? Math.max(16, rect.top + rect.height / 2 - 90) : 120;

  return (
    <AnimatePresence mode="wait">
      {isActive && (
        <motion.div
          key="onboarding-overlay-root"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ position: 'fixed', inset: 0, zIndex: 19990, pointerEvents: 'none' }}
        >
          {/* ── Dark overlay using box-shadow inset trick ───────────────── */}
          {rect && (
            <motion.div
              key={`highlight-${currentStep}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
              style={{
                position: 'fixed',
                top: highlightTop,
                left: highlightLeft,
                width: highlightWidth,
                height: highlightHeight,
                borderRadius: 8,
                // Large box-shadow creates the dark overlay effect around the highlight
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.52)',
                zIndex: 19991,
                pointerEvents: 'none',
                outline: '2.5px solid #2ec291',
                outlineOffset: 1,
              }}
            />
          )}

          {/* ── Tooltip Card ──────────────────────────────────────────────── */}
          <motion.div
            key={`tooltip-${currentStep}`}
            initial={{ opacity: 0, x: -12, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -8, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 380, damping: 28 }}
            style={{
              position: 'fixed',
              top: tooltipTop,
              left: tooltipLeft,
              width: 280,
              background: '#ffffff',
              borderRadius: 14,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              padding: '18px 20px 16px',
              zIndex: 19999,
              pointerEvents: 'all',
              border: '1px solid #e2e8f0',
            }}
          >
            {/* Accent top bar */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: 'linear-gradient(90deg, #2ec291, #1a7a5e)',
                borderRadius: '14px 14px 0 0',
              }}
            />

            {/* Close button */}
            <button
              id="onboarding-close-btn"
              onClick={skipTour}
              style={{
                position: 'absolute',
                top: 10,
                right: 12,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#94a3b8',
                fontSize: 18,
                lineHeight: 1,
                padding: 2,
              }}
              aria-label="Tutup tutorial"
            >
              ×
            </button>

            {/* Step title */}
            <p
              style={{
                margin: '4px 20px 6px 0',
                fontWeight: 700,
                fontSize: 14,
                color: '#0f172a',
                lineHeight: 1.4,
              }}
            >
              {step.id === 'welcome'
                ? `Selamat datang, ${Auth?.data?.data?.merchant?.businessName || 'Merchant'}!`
                : step.title}
            </p>

            {/* Step description */}
            <p
              style={{
                margin: '0 0 14px',
                fontSize: 13,
                color: '#475569',
                lineHeight: 1.6,
              }}
            >
              {step.description}
            </p>

            {/* Footer: progress + buttons */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
                {currentStep + 1} dari {ONBOARDING_TOTAL_STEPS}
              </span>

              <div style={{ display: 'flex', gap: 8 }}>
                {currentStep > 0 && (
                  <button
                    id="onboarding-prev-btn"
                    onClick={prevStep}
                    style={{
                      background: 'transparent',
                      border: '1px solid #cbd5e1',
                      borderRadius: 8,
                      padding: '6px 14px',
                      fontSize: 13,
                      color: '#475569',
                      cursor: 'pointer',
                      fontWeight: 500,
                      transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#64748b')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#cbd5e1')}
                  >
                    Kembali
                  </button>
                )}
                <motion.button
                  id="onboarding-next-btn"
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => {
                    if (currentStep + 1 >= ONBOARDING_TOTAL_STEPS) {
                      navigate('/');
                    }
                    nextStep();
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #2ec291, #1a9b74)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '6px 18px',
                    fontSize: 13,
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(46,194,145,0.3)',
                  }}
                >
                  {currentStep + 1 >= ONBOARDING_TOTAL_STEPS ? 'Selesai' : 'Lanjut'}
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
